/**
 * @module ui/story-feed
 * @description Handle the Story Feed "View More [Section]" (e.g Canada) button loading.
 */
import { qs, toggleAttribute } from "utils/utils";
import { API_PB_RENDER_FEATURE, API_TIMEOUT } from "lib/gamservices";
import { constructQuery } from "utils/urls";
import logger from "utils/logger";
import { fetchTimeout, validStatus, handleJSON, handleRenderFeature } from "utils/fetch";

const debugTag = "[STORY-FEED]"; // for logger

/**
 * @method init
 * @description Bind click event for "View More [Section]" button.
 */
export default function init() {
  const button = qs(".js-sf-automated-more");
  const before = qs(".js-sf-automated-before");
  if (!button || !before) {
    return;
  }

  // swap current button text for loading text, and vise-versa
  let loadingText = button.dataset.loadingText || "Loading News ...";
  const swapText = b => {
    toggleAttribute(b, "disabled");
    [b.innerHTML, loadingText] = [loadingText, b.innerHTML];
  };

  button.addEventListener("click", e => {
    e.preventDefault();
    e.stopPropagation();

    // Always select the most recent dataset rendered.
    // If it's not present, that means no more stories.
    const next = qs(".js-sf-automated-data");
    if (!next) {
      button.parentElement.removeChild(button);
      return;
    }

    // gather all params needed to make API call
    const {
      queryString: q,
      startIndex: from,
      queryCount: size,
      displayCount: display,
      duplicateArray: duplicates // no need to decode
    } = next.dataset;

    swapText(button); // disable button during loading

    // get the story-feed markup and render it
    const payload = { q, from, size, display, duplicates };
    getMoreStories(payload)
      .then(markup => {
        logger.info(`${debugTag} Story-feed success:`, payload);
        next.classList.remove("js-sf-automated-data"); // prevent re-selection of previous dataset
        before.insertAdjacentHTML("beforebegin", markup); // insert stories above button

        // check for the "empty" result set flag
        if (markup.indexOf("FEATURE-EMPTY") >= 0) {
          button.parentElement.removeChild(button);
        }
      })
      .catch(error => {
        logger.error(`${debugTag} Story-feed fail:`, payload, error);
      })
      .then(() => {
        swapText(button); // re-enable button
      });
  });
}

/**
 * @method getMoreStories
 * @description Load some more stories and insert them above the button.
 * @param {Object} c - collection of properties
 * @param {String} c.q - query
 * @param {String} c.from - start index of query
 * @param {String} c.size - count of items to fetch with query
 * @param {String} c.display - count of items to actually display
 * @param {String} c.duplicates - of canonical urls encountered - ["array","as","string","of","urls"]
 * @returns {Promise<String>} of rendered markup
 */
function getMoreStories({ q, from, size, display, duplicates }) {
  const jsonEncode = json => encodeURIComponent(JSON.stringify(json));

  // content config for the feature's content source
  const contentConfig = jsonEncode({ q, from, size });
  logger.info(`${debugTag} contentConfig:`, contentConfig);

  // config for the feature's custom field values
  const customFields = jsonEncode({
    linkTrackFeatureItemsContainerName: "view more articles",
    end: display,
    listDisplayType: "standard",
    showSectionTitle: false,
    loadMore: true,
    noInlineAds: true,
    feedType: "automatedFeed",
    excludeDuplicates: duplicates
  });
  logger.info(`${debugTag} customFields:`, customFields);

  // combine it all and create the GET url
  const params = {
    wrap: false,
    service: "story-feed-query",
    contentConfig,
    customFields
  };

  if (window.location.search.length > 0) {
    params.token = Date.now(); // help prevent server-side caching during QA

    // check for the optional debug flag, so that feature renderer also uses it
    if (/_debugLoadMore=true/.test(window.location.search)) {
      params._debugLoadMore = true;
    }
  }

  const query = constructQuery(params, true);
  const pbRenderAPI = `${API_PB_RENDER_FEATURE}/global/story-feed?${query}`;
  return fetchTimeout(API_TIMEOUT, fetch(pbRenderAPI))
    .then(validStatus)
    .then(handleJSON)
    .then(handleRenderFeature);
}
